import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import FAQItem from './common/FAQItem';

const FAQ = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            question: "Do I need coding experience?",
            answer: "No. We designed DataHelix for beginners. You start with the basics of SQL and gradually move to complex business logic. No prior programming knowledge is required."
        },
        {
            question: "Who is DataHelix for?",
            answer: "It is for aspiring Data Analysts, Analytics Engineers, and anyone who wants to learn how to answer business questions with data. It’s also great for experienced professionals looking to brush up on advanced SQL patterns."
        },
        {
            question: "How is DataHelix different?",
            answer: "Unlike other platforms that focus on abstract syntax puzzles, DataHelix focuses on Business Case Studies. You solve real problems (e.g., 'Calculate Monthly Retention') using a real database, simulating the actual work of a Data Analyst."
        },
        {
            question: "What skills will I learn?",
            answer: "You will master SQL (PostgreSQL dialect), Data Modeling, Business Logic translation, and Analytical Thinking. We also have modules planned for Python and dbt."
        },
        {
            question: "How long does it take?",
            answer: "The core 'Thinking in Data' curriculum takes about 20-30 hours to complete. However, it is self-paced, so you can learn at your own speed."
        },
        {
            question: "Will I work on real-world projects?",
            answer: "Yes. Every challenge is based on a real-world scenario. You will work with realistic datasets (e-commerce, fintech, SaaS) to solve problems that actual companies face."
        },
        {
            question: "Is DataHelix free?",
            answer: "Yes, DataHelix is currently 100% free for individual learners. Our mission is to democratize data education and bridge the gap between theory and practice."
        },
        {
            question: "What roadmap does DataHelix follow?",
            answer: "We follow the modern data stack roadmap: SQL Fundamentals -> Advanced SQL & Analytics Engineering -> Python for Data Science. We are constantly adding new case studies and modules."
        }
    ];

    const handleToggle = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    // Structured Data for SEO (FAQPage Schema)
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };

    return (
        <section id="faq" className="py-24 px-6 bg-white dark:bg-[#0A1220] transition-colors duration-300 border-t border-gray-200 dark:border-gray-800">
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-16">
                    <span className="text-blue-600 dark:text-blue-400 font-bold uppercase tracking-widest text-sm mb-3 block">
                        Common Questions
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                        Everything you need to know.
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        Got questions about DataHelix? We've got answers.
                    </p>
                </div>

                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <FAQItem
                            key={index}
                            question={faq.question}
                            answer={faq.answer}
                            isOpen={openIndex === index}
                            toggle={() => handleToggle(index)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FAQ;
